import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import Layout from "../components/layout/Layout";
import {
  KPIGrid,
  LeaderTable,
  LeaderRankBarChart,
  LeaderRankBarChartModal,
  PositionDistribution,
  PositionDistributionModal,
  SerpFeaturesChart,
  SeasonalityChart,
  KeywordInsights,
  PeopleAlsoAskReport,
  CompetitorsTable,
  SimilaritiesTable,
  EvolutionChart,
  ComparisonView,
  EditableProjectTitle,
} from "../components/analysis";
import LeaderDetailModal from "../components/analysis/LeaderDetailModal";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Badge from "../components/common/Badge";
import Icon from "../components/common/Icon";
import Card from "../components/common/Card";
import ErrorBanner from "../components/common/ErrorBanner";
import CategoryFilter from "../components/common/CategoryFilter";
import DomainFilter from "../components/common/DomainFilter";
import LocationFilter from "../components/common/LocationFilter";
import { studiesAPI } from "../services/api";
import {
  STATUS_CONFIG,
  POLLING_INTERVAL,
  METRIC_TOOLTIPS,
  SESSION_KEYS,
} from "../utils/constants";
import useSessionList from "../hooks/useSessionList";
import { sanitizeDomainList, protectClientDomain } from "../utils/domainFilters";
import { formatDate } from "../utils/formatters";
import { LANGUAGE_NAMES } from "../utils/languages";
import { getTheme } from "../utils/branding";
import "./ProjectAnalysis.css";

const ProjectAnalysis = () => {
  const { studyId } = useParams();
  const history = useHistory();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [historySubTab, setHistorySubTab] = useState("recurrence");
  const [snapshotDateFrom, setSnapshotDateFrom] = useState("");
  const [snapshotDateTo, setSnapshotDateTo] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [excludedDomains, setExcludedDomains] = useSessionList(
    SESSION_KEYS.EXCLUDED_DOMAINS,
    []
  );
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    setSelectedLocations([]);
  }, [studyId]);

  const availableLocations = React.useMemo(() => {
    if (!study?.keywords) return [];
    const counts = new Map();
    study.keywords.forEach((kw) => {
      if (!kw.formattedAddress) return;
      const current = counts.get(kw.formattedAddress) || 0;
      counts.set(kw.formattedAddress, current + 1);
    });
    const options = Array.from(counts.entries())
      .map(([name, keywordCount]) => ({ name, keywordCount }));
    options.sort((a, b) => a.name.localeCompare(b.name));
    return options;
  }, [study]);
  const [filteredReportData, setFilteredReportData] = useState(null);
  const [loadingFilteredData, setLoadingFilteredData] = useState(false);
  const [leaderModal, setLeaderModal] = useState({
    isOpen: false,
    type: null,
    data: [],
    title: "",
    metricLabel: "",
  });
  const [positionDistributionModal, setPositionDistributionModal] = useState({
    isOpen: false,
    title: "Répartition des positions par domaine",
    data: [],
  });

  const closePositionDistributionModal = () => {
    setPositionDistributionModal((prev) => ({ ...prev, isOpen: false }));
  };

  const [leaderRankModal, setLeaderRankModal] = useState({
    isOpen: false,
    title: "",
    data: [],
  });

  const closeLeaderRankModal = () => {
    setLeaderRankModal((prev) => ({ ...prev, isOpen: false }));
  };
  const [relaunching, setRelaunching] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsError, setSnapshotsError] = useState(null);
  const [showSnapshotPicker, setShowSnapshotPicker] = useState(false);
  const [scheduling, setScheduling] = useState({
    scheduleId: null,
    enabled: false,
    startDate: "",
    endDate: "",
    frequencyType: "WEEK",
    frequencyValue: 1,
    nextRunAt: null
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchStudy = useCallback(async () => {
    try {
      setError(null);
      const data = await studiesAPI.get(studyId);
      setStudy(data);
    } catch (err) {
      console.error("Error fetching study:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  const handleRename = useCallback(async (newName) => {
    const updated = await studiesAPI.updateName(studyId, newName);
    setStudy((prev) => prev ? { ...prev, name: updated?.name ?? null } : prev);
  }, [studyId]);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await studiesAPI.getCategories(studyId);
      if (categoriesData && categoriesData.categories && categoriesData.categories.length > 0) {
        setCategories(categoriesData.categories);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  }, [studyId]);

  const fetchSnapshots = useCallback(async () => {
    try {
      setSnapshotsError(null);
      const data = await studiesAPI.getSnapshots(studyId);
      if (data && data.snapshots) {
        setSnapshots(data.snapshots);
      }
    } catch (err) {
      console.error("Error fetching snapshots:", err);
      setSnapshots([]);
      setSnapshotsError(err.message);
    }
  }, [studyId]);

  const fetchSchedule = useCallback(async () => {
    try {
      const data = await studiesAPI.getSchedule(studyId);
      if (data) {
        setScheduling({
          scheduleId: data.scheduleId,
          enabled: data.isActive,
          startDate: data.startDate ? data.startDate.split('T')[0] : "",
          endDate: data.endDate ? data.endDate.split('T')[0] : "",
          frequencyType: data.frequencyType || "WEEK",
          frequencyValue: data.frequencyValue || 1,
          nextRunAt: data.nextRunAt
        });
      }
    } catch (err) {
      if (err.status !== 404) {
        console.error("Error fetching schedule:", err);
      }
    }
  }, [studyId]);

  const saveSchedule = useCallback(async () => {
    try {
      setSavingSchedule(true);
      const scheduleData = {
        startDate: scheduling.startDate,
        endDate: scheduling.endDate,
        frequencyType: scheduling.frequencyType,
        frequencyValue: scheduling.frequencyValue
      };
      const result = await studiesAPI.saveSchedule(studyId, scheduleData);
      setScheduling({
        scheduleId: result.scheduleId,
        enabled: result.isActive,
        startDate: result.startDate ? result.startDate.split('T')[0] : scheduling.startDate,
        endDate: result.endDate ? result.endDate.split('T')[0] : scheduling.endDate,
        frequencyType: result.frequencyType || scheduling.frequencyType,
        frequencyValue: result.frequencyValue || scheduling.frequencyValue,
        nextRunAt: result.nextRunAt
      });
    } catch (err) {
      console.error("Error saving schedule:", err);
      alert('Erreur lors de la sauvegarde de la planification');
    } finally {
      setSavingSchedule(false);
    }
  }, [studyId, scheduling.startDate, scheduling.endDate, scheduling.frequencyType, scheduling.frequencyValue]);

  const deleteSchedule = useCallback(async () => {
    try {
      await studiesAPI.deleteSchedule(studyId);
      setScheduling({
        scheduleId: null,
        enabled: false,
        startDate: "",
        endDate: "",
        frequencyType: "WEEK",
        frequencyValue: 1,
        nextRunAt: null
      });
    } catch (err) {
      console.error("Error deleting schedule:", err);
    }
  }, [studyId]);

  useEffect(() => {
    fetchStudy();
    fetchCategories();
    fetchSnapshots();
    fetchSchedule();
  }, [fetchStudy, fetchCategories, fetchSnapshots, fetchSchedule]); 

  useEffect(() => {
    if (!showSnapshotPicker) return;
    
    const handleClickOutside = (event) => {
      if (!event.target.closest('.analysis-date-wrapper')) {
        setShowSnapshotPicker(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSnapshotPicker]);

  useEffect(() => {
    if (!study || ["Completed", "Failed", "Cancelled"].includes(study.status)) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchStudy();
    }, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [study, fetchStudy]);

  useEffect(() => {
    const fetchFilteredReport = async () => {
      if (!study || study.status !== "Completed") return;

      const allCategoriesSelected = categories.length > 0 &&
        selectedCategories.length === categories.length;
      const allLocationsSelected = availableLocations.length > 0 &&
        selectedLocations.length === availableLocations.length;

      const categoryFilterActive = categories.length > 0
        && selectedCategories.length > 0
        && !allCategoriesSelected;
      const locationFilterActive = availableLocations.length > 0
        && selectedLocations.length > 0
        && !allLocationsSelected;

      if (!categoryFilterActive && !locationFilterActive) {
        setFilteredReportData(null);
        return;
      }

      const categoriesPayload = categoryFilterActive ? selectedCategories : [];
      const locationsPayload = locationFilterActive ? selectedLocations : [];

      setLoadingFilteredData(true);
      try {
        const data = await studiesAPI.getFilteredReport(studyId, categoriesPayload, locationsPayload);
        setFilteredReportData(data);
      } catch (err) {
        console.error("Error fetching filtered report:", err);
        setFilteredReportData(null);
      } finally {
        setLoadingFilteredData(false);
      }
    };

    fetchFilteredReport();
  }, [studyId, study, categories, selectedCategories, availableLocations, selectedLocations]);

  const handleBack = () => {
    history.push("/");
  };

  const handleRelaunch = useCallback(async () => {
    if (relaunching) return;
    
    try {
      setRelaunching(true);
      const response = await studiesAPI.relaunch(studyId);
      if (response.studyId) {
        history.push(`/projets/${response.studyId}`);
      } else {
        await fetchSnapshots();
      }
    } catch (err) {
      console.error('Error relaunching study:', err);
      setError(err.message);
    } finally {
      setRelaunching(false);
    }
  }, [studyId, relaunching, fetchSnapshots, history]);

  const handleCancel = useCallback(async () => {
    if (cancelling) return;

    if (!window.confirm("Annuler cette analyse en cours ? Les donnees deja collectees seront conservees.")) {
      return;
    }

    try {
      setCancelling(true);
      await studiesAPI.cancel(studyId);
      await fetchStudy();
    } catch (err) {
      console.error('Error cancelling study:', err);
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  }, [studyId, cancelling, fetchStudy]);

  const handleRetryFailedStudy = useCallback(async () => {
    if (relaunching) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir relancer cette analyse ? Toutes les données existantes seront supprimées.')) {
      return;
    }
    
    try {
      setRelaunching(true);
      const response = await studiesAPI.retry(studyId);
      if (response && response.newStudyId) {
        history.push(`/projets/${response.newStudyId}`);
      }
    } catch (err) {
      console.error('Error retrying study:', err);
      setError(err.message);
    } finally {
      setRelaunching(false);
    }
  }, [studyId, relaunching, history]);

  const availableDomains = React.useMemo(() => {
    if (!study?.topCompetitors) return [];
    const domains = study.topCompetitors.map(c => c.domain);
    if (study.clientDomain && !domains.includes(study.clientDomain)) {
      domains.unshift(study.clientDomain);
    }
    return domains;
  }, [study]);

  useEffect(() => {
    if (availableDomains.length === 0) return;
    const clientDomain = study?.clientDomain || "";
    setExcludedDomains((previousExcluded) => {
      const availableExclusions = sanitizeDomainList(previousExcluded, availableDomains);
      return protectClientDomain(availableExclusions, clientDomain);
    });
  }, [availableDomains, study, setExcludedDomains]);

  // Check if all categories are selected (= no filter)
  const isAllCategoriesSelected = categories.length > 0 && 
    selectedCategories.length === categories.length;

  // Filter keywords by category + location
  const getFilteredKeywords = useCallback(() => {
    if (!study || !study.keywords) return [];

    const categoryFilterActive = categories.length > 0 && selectedCategories.length > 0 && !isAllCategoriesSelected;
    const locationFilterActive = selectedLocations.length > 0 && selectedLocations.length < availableLocations.length;

    if (!categoryFilterActive && !locationFilterActive) {
      return study.keywords;
    }

    return study.keywords.filter(kw => {
      if (categoryFilterActive) {
        const kwCategory = kw.category ? kw.category.toLowerCase().trim() : null;
        if (!kwCategory) return false;
        const categoryMatches = selectedCategories.some(sc => sc.toLowerCase().trim() === kwCategory);
        if (!categoryMatches) return false;
      }

      if (locationFilterActive) {
        if (!kw.formattedAddress) return false;
        if (!selectedLocations.includes(kw.formattedAddress)) return false;
      }

      return true;
    });
  }, [study, selectedCategories, categories, isAllCategoriesSelected, selectedLocations, availableLocations]);

  const getFilteredReport = useCallback(() => {
    if (!study || !study.report) return null;

    const allLocationsSelected = availableLocations.length > 0 &&
      selectedLocations.length === availableLocations.length;

    const categoryFilterActive = categories.length > 0
      && selectedCategories.length > 0
      && !isAllCategoriesSelected;
    const locationFilterActive = availableLocations.length > 0
      && selectedLocations.length > 0
      && !allLocationsSelected;

    if (!categoryFilterActive && !locationFilterActive) {
      return study.report;
    }

    if (filteredReportData) {
      return filteredReportData;
    }

    return study.report;
  }, [study, selectedCategories, categories, isAllCategoriesSelected, filteredReportData, availableLocations, selectedLocations]);

  const getAllLeaders = useCallback((metric) => {
    const competitorsSource = filteredReportData?.topCompetitors || study?.topCompetitors;
    if (!competitorsSource || competitorsSource.length === 0) {
      return [];
    }

    const calculateTop10 = (report) => {
      return (report.rank1To3Count || 0) + (report.rank4To5Count || 0) + (report.rank6To10Count || 0);
    };

    const clientDomain = study?.clientDomain;
    const reportSource = filteredReportData || study?.report;

    let allDomains = competitorsSource
      .filter(c => !excludedDomains.includes(c.domain))
      .map((c) => ({
        domain: c.domain,
        shareOfVoice: c.shareOfVoice,
        estimatedTraffic: c.estimatedTraffic,
        presenceCount: c.presenceCount,
        top10Count: calculateTop10(c),
        isClient: c.domain === clientDomain,
      }));

    // Add client domain if not already in competitors list and not excluded
    const clientInList = allDomains.some(d => d.domain === clientDomain);
    const clientExcluded = excludedDomains.includes(clientDomain);
    if (!clientInList && !clientExcluded && reportSource && clientDomain) {
      allDomains.push({
        domain: clientDomain,
        shareOfVoice: reportSource.shareOfVoice || 0,
        estimatedTraffic: reportSource.estimatedTraffic || 0,
        presenceCount: reportSource.presenceCount || 0,
        top10Count: calculateTop10(reportSource),
        isClient: true,
      });
    }

    allDomains.sort((a, b) => b[metric] - a[metric]);

    return allDomains.map((item, index) => {
      const value = item[metric];
      let formattedValue;
      if (metric === "shareOfVoice") {
        formattedValue = `${value.toFixed(1)}%`;
      } else if (metric === "estimatedTraffic") {
        formattedValue =
          value >= 1000
            ? `${(value / 1000).toFixed(1)}K`
            : value.toString();
      } else {
        formattedValue = value.toString();
      }

      return {
        rank: index + 1,
        domain: item.domain,
        value,
        formattedValue,
        isClient: item.isClient,
      };
    });
  }, [study, filteredReportData, excludedDomains]);

  const getLeadersDistribution = useCallback((metric) => {
    const competitorsSource = filteredReportData?.topCompetitors || study?.topCompetitors;
    if (!competitorsSource || competitorsSource.length === 0) {
      return [];
    }

    const segmentedDomains = competitorsSource.map((c) => {
      const sortValue = c[metric] || 0;
      return {
        domain: c.domain,
        segments: {
          rank1To5: (c.rank1To3Count || 0) + (c.rank4To5Count || 0),
          rank6To10: c.rank6To10Count || 0,
          rank11To20: c.rank11To20Count || 0,
          rank21Plus: c.rank21PlusCount || 0,
        },
        total: c.presenceCount || 0,
        sortValue,
        displayValue: `${sortValue.toFixed(1)}%`,
      };
    });

    const sortedDomains = [...segmentedDomains].sort((a, b) => b.sortValue - a.sortValue);

    return sortedDomains;
  }, [study, filteredReportData]);

  const getTopLeaders = useCallback((metric) => {
    const allLeaders = getAllLeaders(metric);
    return allLeaders.slice(0, 5).map((item) => ({
      rank: item.rank,
      domain: item.domain,
      value: item.formattedValue,
    }));
  }, [getAllLeaders]);

  const handleViewMoreShareOfVoice = () => {
    const allLeaders = getAllLeaders("shareOfVoice");
    setLeaderModal({
      isOpen: true,
      type: "shareOfVoice",
      data: allLeaders,
      title: "Leaders en part de voix",
      metricLabel: "Part de voix",
    });
  };

  const handleViewMoreEstimatedTraffic = () => {
    const allLeaders = getAllLeaders("estimatedTraffic");
    setLeaderModal({
      isOpen: true,
      type: "estimatedTraffic",
      data: allLeaders,
      title: "Leaders en trafic estimé",
      metricLabel: "Trafic estimé",
    });
  };

  const handleViewMoreTop10 = () => {
    const allLeaders = getAllLeaders("top10Count");
    setLeaderModal({
      isOpen: true,
      type: "top10Count",
      data: allLeaders,
      title: "Leaders en Top 10 KW (première page)",
      metricLabel: "Top 10",
    });
  };

  const handleCloseModal = () => {
    setLeaderModal({
      isOpen: false,
      type: null,
      data: [],
      title: "",
      metricLabel: "",
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="analysis-loading">
          <LoadingSpinner size="lg" text="Chargement de l'analyse..." />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="analysis-error">
          <Icon name="AlertCircle" size={48} color="var(--color-error)" />
          <h2>Erreur</h2>
          <p>{error}</p>
          <button onClick={handleBack}>Retour au tableau de bord</button>
        </div>
      </Layout>
    );
  }

  if (!study) {
    return (
      <Layout>
        <div className="analysis-error">
          <Icon name="FileQuestion" size={48} color="var(--color-gray-400)" />
          <h2>Étude introuvable</h2>
          <button onClick={handleBack}>Retour au tableau de bord</button>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[study.status] || STATUS_CONFIG.Pending;
  const isInProgress = !["Completed", "Failed", "Cancelled"].includes(study.status);
  const isAdsup = getTheme() === 'adsup';

  return (
    <Layout>
      <div className="project-analysis">
        {/* Header */}
        {isAdsup ? (
          <div className="analysis-header analysis-header-adsup">
            <button className="back-button" onClick={handleBack}>
              <Icon name="ArrowLeft" size={20} />
              Retour au dashboard
            </button>

            <EditableProjectTitle
              name={study.name}
              fallback={`Analyse ${study.clientDomain}`}
              onRename={handleRename}
            />

            <div className="analysis-header-meta analysis-header-meta--start">
              {study.languageId && (
                <span className="analysis-lang-badge">{LANGUAGE_NAMES[study.languageId] || study.languageId}</span>
              )}
              <Badge variant={statusConfig.color}>{statusConfig.badge}</Badge>
            </div>

            <div className="header-separator" />

            <div className="analysis-header-meta">
              <span className="analysis-meta-item">{study.totalKeywords} mots-clés</span>
              {study.createdAt && (
                <div className="analysis-date-wrapper">
                  <button
                    className="analysis-date-btn"
                    onClick={() => setShowSnapshotPicker(!showSnapshotPicker)}
                    title={snapshots.length > 1 ? "Voir l'historique des analyses" : "Date de l'analyse"}
                  >
                    <Icon name="Calendar" size={14} />
                    {formatDate(study.createdAt)}
                    {snapshots.length > 1 && (
                      <Icon name="ChevronDown" size={12} className="dropdown-arrow" />
                    )}
                  </button>
                  {showSnapshotPicker && snapshots.length > 1 && (
                    <div className="snapshot-picker-dropdown">
                      <div className="snapshot-picker-header">Historique des analyses</div>
                      {snapshots.map((snapshot) => (
                        <button
                          key={snapshot.studyId}
                          className={`snapshot-item ${snapshot.studyId === studyId ? 'active' : ''}`}
                          onClick={() => {
                            setShowSnapshotPicker(false);
                            if (snapshot.studyId !== studyId) {
                              history.push(`/projets/${snapshot.studyId}`);
                            }
                          }}
                        >
                          <span className="snapshot-number">#{snapshot.snapshotNumber}</span>
                          <span className="snapshot-date">{formatDate(snapshot.createdAt)}</span>
                          <span className={`snapshot-status ${snapshot.status.toLowerCase()}`}>
                            {snapshot.status === 'Completed' ? 'OK' :
                             snapshot.status === 'Failed' ? 'Err' :
                             snapshot.status === 'Cancelled' ? 'Ann' : '...'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {study.userDisplayName && (
                <span className="analysis-author">
                  <Icon name="User" size={14} />
                  {study.userDisplayName}
                </span>
              )}
            </div>

            <div className="analysis-header-filters">
              {study.status === "Completed" && (
                <>
                  {categories.length > 0 && (
                    <CategoryFilter
                      categories={categories}
                      selectedCategories={selectedCategories}
                      onChange={setSelectedCategories}
                    />
                  )}
                  {availableDomains.length > 0 && (
                    <DomainFilter
                      domains={availableDomains}
                      excludedDomains={excludedDomains}
                      clientDomain={study.clientDomain}
                      onChange={setExcludedDomains}
                    />
                  )}
                  {availableLocations.length > 0 && (
                    <LocationFilter
                      locations={availableLocations}
                      selectedLocations={selectedLocations}
                      onChange={setSelectedLocations}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="analysis-header">
            <button className="back-button" onClick={handleBack}>
              <Icon name="ArrowLeft" size={20} />
              Retour au dashboard
            </button>
            <div className="analysis-title-row">
              <div>
                <div className="analysis-title-with-lang">
                  <EditableProjectTitle
                    name={study.name}
                    fallback={`Analyse ${study.clientDomain}`}
                    onRename={handleRename}
                  />
                  {study.languageId && (
                    <span className="analysis-lang-badge">{LANGUAGE_NAMES[study.languageId] || study.languageId}</span>
                  )}
                </div>
                <div className="analysis-meta">
                  <span>{study.totalKeywords} mots-clés</span>
                  {study.createdAt && (
                    <div className="analysis-date-wrapper">
                      <button
                        className="analysis-date-btn"
                        onClick={() => setShowSnapshotPicker(!showSnapshotPicker)}
                        title={snapshots.length > 1 ? "Voir l'historique des analyses" : "Date de l'analyse"}
                      >
                        <Icon name="Calendar" size={14} />
                        {formatDate(study.createdAt)}
                        {snapshots.length > 1 && (
                          <Icon name="ChevronDown" size={12} className="dropdown-arrow" />
                        )}
                      </button>
                      {showSnapshotPicker && snapshots.length > 1 && (
                        <div className="snapshot-picker-dropdown">
                          <div className="snapshot-picker-header">Historique des analyses</div>
                          {snapshots.map((snapshot) => (
                            <button
                              key={snapshot.studyId}
                              className={`snapshot-item ${snapshot.studyId === studyId ? 'active' : ''}`}
                              onClick={() => {
                                setShowSnapshotPicker(false);
                                if (snapshot.studyId !== studyId) {
                                  history.push(`/projets/${snapshot.studyId}`);
                                }
                              }}
                            >
                              <span className="snapshot-number">#{snapshot.snapshotNumber}</span>
                              <span className="snapshot-date">{formatDate(snapshot.createdAt)}</span>
                              <span className={`snapshot-status ${snapshot.status.toLowerCase()}`}>
                                {snapshot.status === 'Completed' ? 'OK' : snapshot.status === 'Failed' ? 'Err' : '...'}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {study.userDisplayName && (
                    <span className="analysis-author">
                      <Icon name="User" size={14} />
                      {study.userDisplayName}
                    </span>
                  )}
                </div>
              </div>
              <div className="analysis-header-actions">
                <Badge variant={statusConfig.color}>{statusConfig.badge}</Badge>
              </div>
            </div>
          </div>
        )}

        {/* Filters (JVWEB only — Adsup filters are inside the header) */}
        {!isAdsup && study.status === "Completed" && (
          <div className="analysis-filters">
            {categories.length > 0 && (
              <CategoryFilter
                categories={categories}
                selectedCategories={selectedCategories}
                onChange={setSelectedCategories}
              />
            )}
            {availableDomains.length > 0 && (
              <DomainFilter
                domains={availableDomains}
                excludedDomains={excludedDomains}
                clientDomain={study.clientDomain}
                onChange={setExcludedDomains}
              />
            )}
            {availableLocations.length > 0 && (
              <LocationFilter
                locations={availableLocations}
                selectedLocations={selectedLocations}
                onChange={setSelectedLocations}
              />
            )}
          </div>
        )}

        {/* In Progress State */}
        {isInProgress && (
          <div className="analysis-progress">
            <LoadingSpinner size="md" />
            <p>{statusConfig.label}</p>
            <button
              type="button"
              className="btn-cancel-analysis"
              onClick={handleCancel}
              disabled={cancelling}
            >
              <Icon name={cancelling ? "Loader" : "XCircle"} size={16} />
              {cancelling ? "Annulation..." : "Annuler l'analyse"}
            </button>
          </div>
        )}

        {/* Failed State */}
        {study.status === "Failed" && (
          <ErrorBanner
            errorType={study.errorType || "UnknownError"}
            errorMessage={study.errorMessage}
            onRetry={handleRetryFailedStudy}
          />
        )}

        {/* Cancelled State */}
        {study.status === "Cancelled" && (
          <div className="analysis-cancelled">
            <Icon name="XCircle" size={40} color="var(--color-gray-500)" />
            <h3>Analyse annulee</h3>
            <p>Vous pouvez relancer une nouvelle analyse depuis le dashboard.</p>
          </div>
        )}

        {/* Completed State */}
        {study.status === "Completed" && study.report && (
          <>
            {/* Tabs */}
            <div className="analysis-tabs">
              <button
                className={`tab ${
                  activeTab === "overview" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("overview")}
              >
                Vue d'ensemble
              </button>
              <button
                className={`tab ${
                  activeTab === "competitors" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("competitors")}
              >
                Top domaines
              </button>
              <button
                className={`tab ${
                  activeTab === "keywords" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("keywords")}
              >
                Analyse des mots clés
              </button>
              <button
                className={`tab ${
                  activeTab === "similarities" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("similarities")}
              >
                Similarités
              </button>
              <button
                className={`tab ${
                  activeTab === "history" ? "tab-active" : ""
                }`}
                onClick={() => setActiveTab("history")}
              >
                Historique
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
              <div className="tab-content">
                {/* KPIs */}
                <KPIGrid report={getFilteredReport()} />

                {/* Leader Tables */}
                <div className="leaders-grid">
                  <LeaderTable
                    title="Leaders en part de voix"
                    data={getTopLeaders("shareOfVoice")}
                    tooltipText={METRIC_TOOLTIPS.SHARE_OF_VOICE}
                    onViewMore={handleViewMoreShareOfVoice}
                    loading={loadingFilteredData}
                  />
                  <LeaderTable
                    title="Leaders en trafic estimé"
                    data={getTopLeaders("estimatedTraffic")}
                    tooltipText={METRIC_TOOLTIPS.ESTIMATED_TRAFFIC}
                    onViewMore={handleViewMoreEstimatedTraffic}
                    loading={loadingFilteredData}
                  />
                  <LeaderTable
                    title="Leaders en Top 10 KW"
                    data={getTopLeaders("top10Count")}
                    tooltipText={METRIC_TOOLTIPS.TOP_10}
                    onViewMore={handleViewMoreTop10}
                    loading={loadingFilteredData}
                  />
                </div>

                {/* Leader Rank Bar Charts (couleur par rang top 3 / 5 / 10) */}
                <div className="charts-row">
                  <LeaderRankBarChart
                    title="Leaders en part de voix"
                    tooltipText={METRIC_TOOLTIPS.LEADERS_SHARE_OF_VOICE}
                    data={getLeadersDistribution("shareOfVoice")}
                    excludedDomains={excludedDomains}
                    onViewAll={() => setLeaderRankModal({
                      isOpen: true,
                      title: "Leaders en part de voix",
                      data: getLeadersDistribution("shareOfVoice"),
                    })}
                  />
                  <LeaderRankBarChart
                    title="Leaders en taux de présence"
                    tooltipText={METRIC_TOOLTIPS.LEADERS_PRESENCE_RATE}
                    data={getLeadersDistribution("presenceRate")}
                    excludedDomains={excludedDomains}
                    onViewAll={() => setLeaderRankModal({
                      isOpen: true,
                      title: "Leaders en taux de présence",
                      data: getLeadersDistribution("presenceRate"),
                    })}
                  />
                </div>

                {/* Position Distribution Chart */}
                <PositionDistribution
                  data={filteredReportData?.positionDistribution || study.positionDistribution || []}
                  excludedDomains={excludedDomains}
                  loading={loadingFilteredData}
                  onViewAll={() => setPositionDistributionModal({
                    isOpen: true,
                    title: "Répartition des positions par domaine",
                    data: filteredReportData?.positionDistribution || study.positionDistribution || [],
                  })}
                />

                {/* SERP Features + Saisonnalite */}
                <div className="charts-row">
                  {getFilteredKeywords().length > 0 && (
                    <SeasonalityChart 
                      keywords={getFilteredKeywords()} 
                      categories={categories}
                    />
                  )}
                  {(filteredReportData?.serpFeaturesDistribution || study.serpFeaturesDistribution)?.length > 0 && (
                    <SerpFeaturesChart 
                      data={filteredReportData?.serpFeaturesDistribution || study.serpFeaturesDistribution} 
                      loading={loadingFilteredData}
                    />
                  )}
                </div>
              </div>
            )}

            {activeTab === "competitors" && (
              <div className="tab-content">
                <CompetitorsTable
                  competitors={(filteredReportData?.topCompetitors || study.topCompetitors || [])
                    .filter(c => !excludedDomains.includes(c.domain))}
                  clientDomain={study.clientDomain}
                  loading={loadingFilteredData}
                />
              </div>
            )}

            {activeTab === "keywords" && (
              <div className="tab-content">
                {/* Keywords Insights Table */}
                <KeywordInsights
                  keywords={getFilteredKeywords()}
                  clientDomain={study.clientDomain}
                  availableDomains={availableDomains}
                />
                <PeopleAlsoAskReport keywords={getFilteredKeywords()} />
              </div>
            )}

            {activeTab === "similarities" && (
              <div className="tab-content">
                {/* Similarities Table */}
                <SimilaritiesTable 
                  studyId={study.studyId} 
                  keywords={getFilteredKeywords()}
                />
              </div>
            )}

            {activeTab === "history" && (
              <div className="tab-content">
                <div className="history-tabs">
                  <button
                    className={`history-tab ${historySubTab === "recurrence" ? "history-tab-active" : ""}`}
                    onClick={() => setHistorySubTab("recurrence")}
                  >
                    Récurrence
                  </button>
                  <button
                    className={`history-tab ${historySubTab === "evolution" ? "history-tab-active" : ""}`}
                    onClick={() => setHistorySubTab("evolution")}
                  >
                    Évolution
                  </button>
                  <button
                    className={`history-tab ${historySubTab === "comparison" ? "history-tab-active" : ""}`}
                    onClick={() => setHistorySubTab("comparison")}
                  >
                    Comparaison
                  </button>
                </div>
                
                {historySubTab === "evolution" && (
                  <EvolutionChart 
                    studyId={study.studyId}
                    onRelaunch={(newStudyId) => history.push(`/projets/${newStudyId}`)}
                  />
                )}
                {historySubTab === "comparison" && (
                  <ComparisonView studyId={study.studyId} />
                )}
                {historySubTab === "recurrence" && (
                  <div className="recurrence-layout">
                    <div className="recurrence-layout-row">
                      {/* Section Relance manuelle */}
                      <Card className="recurrence-section recurrence-relaunch">
                        <div className="recurrence-section-header">
                          <Icon name="RefreshCw" size={24} />
                          <h3>Relancer l'analyse</h3>
                        </div>
                        <p className="recurrence-description">
                          Créez une nouvelle analyse pour capturer l'état actuel des positions SERP.
                        </p>
                        {study.status === "Completed" ? (
                          <button 
                            className="btn-relaunch-main" 
                            onClick={handleRelaunch}
                            disabled={relaunching}
                          >
                            <Icon name={relaunching ? "Loader" : "Play"} size={20} />
                            {relaunching ? 'Relance en cours...' : 'Lancer une nouvelle analyse'}
                          </button>
                        ) : (
                          <div className="recurrence-disabled-hint">
                            <Icon name="Info" size={16} />
                            <span>L'analyse doit être terminée pour pouvoir être relancée.</span>
                          </div>
                        )}
                      </Card>

                      {/* Section Historique */}
                      {snapshotsError && (
                        <Card className="recurrence-section recurrence-history">
                          <div className="recurrence-section-header">
                            <Icon name="History" size={24} />
                            <h3>Historique des analyses</h3>
                          </div>
                          <div className="recurrence-history-error">
                            <Icon name="AlertCircle" size={24} color="var(--color-error)" />
                            <p>Impossible de charger l'historique des analyses.</p>
                          </div>
                        </Card>
                      )}
                      {!snapshotsError && snapshots.length > 0 && (
                        <Card className="recurrence-section recurrence-history">
                          <div className="recurrence-section-header">
                            <Icon name="History" size={24} />
                            <h3>Historique des analyses</h3>
                            <span className="snapshot-count">{snapshots.length} analyse{snapshots.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="snapshots-filter-range">
                            <label className="snapshots-filter">
                              <Icon name="Calendar" size={16} />
                              <input
                                type="date"
                                value={snapshotDateFrom}
                                onChange={(e) => setSnapshotDateFrom(e.target.value)}
                                className="snapshots-filter-input"
                                placeholder="Du"
                              />
                            </label>
                            <span className="snapshots-filter-separator">→</span>
                            <label className="snapshots-filter">
                              <Icon name="Calendar" size={16} />
                              <input
                                type="date"
                                value={snapshotDateTo}
                                onChange={(e) => setSnapshotDateTo(e.target.value)}
                                className="snapshots-filter-input"
                                placeholder="Au"
                              />
                            </label>
                            {(snapshotDateFrom || snapshotDateTo) && (
                              <button 
                                className="snapshots-filter-clear"
                                onClick={() => { setSnapshotDateFrom(""); setSnapshotDateTo(""); }}
                              >
                                <Icon name="X" size={14} />
                              </button>
                            )}
                          </div>
                          <div className="snapshots-list">
                            {snapshots
                              .filter(s => {
                                if (!snapshotDateFrom && !snapshotDateTo) return true;
                                const snapshotDate = new Date(s.createdAt).toISOString().split('T')[0];
                                if (snapshotDateFrom && snapshotDateTo) {
                                  return snapshotDate >= snapshotDateFrom && snapshotDate <= snapshotDateTo;
                                }
                                if (snapshotDateFrom) return snapshotDate >= snapshotDateFrom;
                                if (snapshotDateTo) return snapshotDate <= snapshotDateTo;
                                return true;
                              })
                              .map((snapshot) => (
                              <div
                                key={snapshot.studyId}
                                className={`snapshot-row ${snapshot.studyId === studyId ? 'current' : ''}`}
                                onClick={() => {
                                  if (snapshot.studyId !== studyId) {
                                    history.push(`/projets/${snapshot.studyId}`);
                                  }
                                }}
                              >
                                <div className="snapshot-row-left">
                                  <span className="snapshot-badge">#{snapshot.snapshotNumber}</span>
                                  <span className="snapshot-row-date">{formatDate(snapshot.createdAt)}</span>
                                </div>
                                <div className="snapshot-row-right">
                                  <Badge variant={
                                    snapshot.status === 'Completed' ? 'success' :
                                    snapshot.status === 'Failed' ? 'error' :
                                    snapshot.status === 'Cancelled' ? 'default' : 'warning'
                                  }>
                                    {snapshot.status === 'Completed' ? 'Terminee' :
                                     snapshot.status === 'Failed' ? 'Echouee' :
                                     snapshot.status === 'Cancelled' ? 'Annulee' : 'En cours'}
                                  </Badge>
                                  {snapshot.studyId === studyId && (
                                    <span className="current-indicator">Actuelle</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                    </div>

                    {/* Planification automatique - Full width */}
                    <Card className="recurrence-section recurrence-planning">
                      <div className="recurrence-section-header">
                        <Icon name="Calendar" size={24} />
                        <h3>Planification automatique</h3>
                        <label className="scheduling-toggle">
                          <input
                            type="checkbox"
                            checked={scheduling.enabled}
                            onChange={(e) => {
                              const newEnabled = e.target.checked;
                              setScheduling(prev => ({ ...prev, enabled: newEnabled }));
                              if (!newEnabled && scheduling.scheduleId) {
                                deleteSchedule();
                              }
                            }}
                          />
                          <span className="scheduling-toggle-slider"></span>
                        </label>
                      </div>
                      
                      {scheduling.enabled ? (
                        <div className="scheduling-form">
                          <div className="scheduling-row">
                            <div className="scheduling-field">
                              <label>Date de début</label>
                              <input
                                type="date"
                                value={scheduling.startDate}
                                onChange={(e) => setScheduling(prev => ({ ...prev, startDate: e.target.value }))}
                                className="scheduling-input"
                              />
                            </div>
                            <span className="scheduling-separator">à</span>
                            <div className="scheduling-field">
                              <label>Date de fin</label>
                              <input
                                type="date"
                                value={scheduling.endDate}
                                onChange={(e) => setScheduling(prev => ({ ...prev, endDate: e.target.value }))}
                                className="scheduling-input"
                              />
                            </div>
                          </div>
                          
                          <div className="scheduling-field scheduling-frequency">
                            <label>Fréquence</label>
                            <div className="frequency-selector">
                              <span>Toutes les</span>
                              <input
                                type="number"
                                min="1"
                                max="52"
                                value={scheduling.frequencyValue}
                                onChange={(e) => setScheduling(prev => ({ ...prev, frequencyValue: Math.max(1, Math.min(52, parseInt(e.target.value) || 1)) }))}
                                className="frequency-value-input"
                              />
                              <div className="frequency-type-options">
                                {[
                                  { value: "WEEK", label: "semaine", labelPlural: "semaines" },
                                  { value: "MONTH", label: "mois", labelPlural: "mois" },
                                  { value: "YEAR", label: "année", labelPlural: "années" }
                                ].map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={`frequency-option ${scheduling.frequencyType === opt.value ? 'active' : ''}`}
                                    onClick={() => setScheduling(prev => ({ ...prev, frequencyType: opt.value }))}
                                  >
                                    {scheduling.frequencyValue > 1 ? opt.labelPlural : opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          <div className="scheduling-actions">
                            {scheduling.nextRunAt && (
                              <span className="scheduling-next-run">
                                Prochaine exécution : {new Date(scheduling.nextRunAt).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            <button
                              className="btn-save-schedule"
                              disabled={!scheduling.startDate || !scheduling.endDate || savingSchedule}
                              onClick={saveSchedule}
                            >
                              {savingSchedule ? 'Enregistrement...' : scheduling.scheduleId ? 'Mettre à jour' : 'Enregistrer la planification'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="recurrence-description">
                          Activez la planification pour programmer des analyses automatiques.
                        </p>
                      )}
                    </Card>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Leader Detail Modal */}
        <LeaderDetailModal
          isOpen={leaderModal.isOpen}
          onClose={handleCloseModal}
          title={leaderModal.title}
          data={leaderModal.data}
          metricLabel={leaderModal.metricLabel}
        />

        {/* Position Distribution Modal */}
        <PositionDistributionModal
          isOpen={positionDistributionModal.isOpen}
          onClose={closePositionDistributionModal}
          title={positionDistributionModal.title}
          data={positionDistributionModal.data}
          excludedDomains={excludedDomains}
        />

        {/* Leader Rank Bar Chart Modal */}
        <LeaderRankBarChartModal
          isOpen={leaderRankModal.isOpen}
          onClose={closeLeaderRankModal}
          title={leaderRankModal.title}
          data={leaderRankModal.data}
          excludedDomains={excludedDomains}
        />
      </div>
    </Layout>
  );
};

export default ProjectAnalysis;

